import { useState } from "react";
import { styled } from "@mui/system";
import { Box, Stack, Switch, TextField, Typography, Button } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./QuillEditor.scss";
import useEventListeners from "@arelia-hooks/useEventListeners";
import logoSrc from "@arelia-assets/arelia.png";

const CustomTextField = styled(TextField)({
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ccc",
  },
  "& .MuiOutlinedInput-root": {
    "&:hover": {
      ".MuiOutlinedInput-notchedOutline": {
        borderColor: "#ccc",
      }
    },
    "&.Mui-focused": {
      ".MuiOutlinedInput-notchedOutline": {
        borderWidth: 1
      }
    }
  }
});

type Props = {
  handleChange: (text: string) => void;
  onCloseEventName: string;
  message: string;
}

export default function TextEditor({ handleChange, onCloseEventName, message }: Props) {
  const [editorHtml, setEditorHtml] = useState(message);
  const [rawHtml, setRawHtml] = useState(message);
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleChangeRawHtml = (html: string) => {
    setRawHtml(html);
    handleChange(html);
  }

  const handleChangeEditorHtml = (html: string) => {
    setEditorHtml(html);
    handleChange(html);
  }

  const handleToggleEditorView = () => {
    const isEditingRaw = showRawHtml;

    setShowRawHtml(!isEditingRaw);
    syncViews(isEditingRaw)
  }

  const syncViews = (fromRaw: boolean) => {
    if (fromRaw) setEditorHtml(rawHtml);
    else setRawHtml(editorHtml);
  }

  const clearHtml = () => {
    setEditorHtml("");
    setRawHtml("");
  }

  const handleShowPreview = () => {
    setShowPreview(!showPreview)
  }

  useEventListeners([onCloseEventName], () => {
    clearHtml();
  });

  return (
    <Box display="flex" flexDirection="column">
      <Stack direction="row" alignItems="center" spacing={1} minHeight="50px">
        <Typography>Edit</Typography>
        <Switch onChange={handleShowPreview} />
        <Typography>Preview</Typography>
      </Stack>

      {!showPreview &&
        <Box className={showRawHtml ? "showRaw" : ""}>
          <ReactQuill value={editorHtml} onChange={handleChangeEditorHtml} />

          <Box className="raw-editor">
            <CustomTextField
              multiline
              variant="outlined"
              rows={18}
              sx={{ width: "100%", height: "100%" }}
              InputProps={{ sx: { height: "100%" } }}
              value={rawHtml}
              onChange={(e) => handleChangeRawHtml(e.target.value)}
            />
          </Box>

          <Box>
            <Button
              sx={{ mt: 1 }}
              variant="contained"
              size="large"
              onClick={handleToggleEditorView}
            >
              {showRawHtml ? "View Rich Text Editor" : "View Raw HTML"}
            </Button>
          </Box>
        </Box>}

      {showPreview &&
        <Box mt={2}>
          <Box sx={{ display: "flex", gap: "10px" }}>
            <img src={logoSrc} style={{ width: "48px", height: "48px" }} alt="avatar" />
            <Typography sx={{ fontSize: "34px", fontWeight: "700" }}>Arelia</Typography>
          </Box>

          <Typography variant="subtitle2" color="secondary">
            <div dangerouslySetInnerHTML={{ __html: editorHtml }} />
          </Typography>
        </Box>
      }
    </Box>
  )
}